import type { ColumnDto } from '@kanban/shared';
import { PRIORITY_LABELS, parseQuickCapture } from '@kanban/shared';
import { useMemo, useState } from 'react';
import { useQuickCard } from '../lib/boards.js';
import { formatDueLabel } from '../lib/format.js';

/**
 * Schnellerfassung (SPEC 9.5).
 *
 * Die Vorschau laeuft mit demselben geteilten Modul, das auch der Server
 * benutzt. Sie ist nicht Zierde, sondern der Grund, warum die Syntax
 * benutzbar ist: man sieht vor dem Absenden, was die App verstanden hat, und
 * merkt einen Tippfehler, bevor die Karte falsch im Board liegt.
 */
export function QuickCaptureSheet({
  boardId,
  column,
  onClose,
}: {
  boardId: string;
  column: ColumnDto;
  onClose: () => void;
}) {
  const [text, setText] = useState('');
  const quickCard = useQuickCard(boardId);

  // `new Date()` bei jeder Eingabe: „morgen" soll auch dann stimmen, wenn das
  // Sheet ueber Mitternacht offen steht.
  const parsed = useMemo(() => parseQuickCapture(text, new Date()), [text]);
  const canSubmit = parsed.title.length > 0 && !quickCard.isPending;

  function submit() {
    if (!canSubmit) return;
    quickCard.mutate({ columnId: column.id, text }, { onSuccess: onClose });
  }

  return (
    <div
      className="fixed inset-0 z-40 flex items-end justify-center bg-[rgb(0_0_0/45%)] md:items-center"
      role="dialog"
      aria-modal="true"
      aria-label="Schnellerfassung"
      data-testid="quick-capture"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="w-full rounded-t-[var(--radius-lg)] bg-surface p-4 shadow-[var(--shadow-3)] md:max-w-lg md:rounded-[var(--radius-lg)]">
        <h2 className="text-sm font-semibold">Neue Karte in „{column.title}"</h2>

        {/*
          Fokus beim Oeffnen: das Sheet erscheint nur auf ausdrueckliches
          Antippen, und ohne Fokus muesste man ein zweites Mal ins Feld
          tippen, bevor die Tastatur kommt.
        */}
        <input
          autoFocus
          data-testid="quick-capture-input"
          value={text}
          onChange={(event) => setText(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') submit();
            if (event.key === 'Escape') onClose();
          }}
          placeholder="Müll rausbringen @lena morgen !hoch #haushalt"
          aria-label="Aufgabe"
          className="mt-3 w-full rounded-[var(--radius)] border border-border bg-surface px-3 py-2"
          style={{ minHeight: 'var(--touch-target)' }}
        />

        <div className="mt-3 min-h-16 rounded-[var(--radius)] bg-surface-muted p-3 text-sm">
          {text.trim() === '' ? (
            <p className="text-xs text-muted">
              <code>@name</code> weist zu, <code>#label</code> vergibt ein Label,{' '}
              <code>!hoch</code> setzt die Priorität. Datum: <code>heute</code>,{' '}
              <code>morgen</code>, <code>Freitag</code>, <code>in 3 Tagen</code>,{' '}
              <code>24.12.</code> — dazu <code>14:30</code> oder <code>9 Uhr</code>.
            </p>
          ) : (
            <>
              <p data-testid="quick-capture-title" className="font-medium">
                {parsed.title || <span className="text-muted">Titel fehlt noch</span>}
              </p>

              <div className="mt-2 flex flex-wrap gap-1.5 text-xs">
                {parsed.dueAt && (
                  <span
                    data-testid="quick-capture-due"
                    className="rounded-full bg-info-soft px-2 py-0.5 text-info"
                  >
                    {formatDueLabel(parsed.dueAt, parsed.dueHasTime)}
                  </span>
                )}
                {parsed.priority > 0 && (
                  <span className="rounded-full bg-caution-soft px-2 py-0.5 text-caution">
                    {PRIORITY_LABELS[parsed.priority]}
                  </span>
                )}
                {parsed.assigneeNames.map((name) => (
                  <span
                    key={name}
                    data-testid="quick-capture-assignee"
                    className="rounded-full bg-accent-soft px-2 py-0.5 text-accent"
                  >
                    @{name}
                  </span>
                ))}
                {parsed.labelNames.map((name) => (
                  <span
                    key={name}
                    className="rounded-full border border-border-strong px-2 py-0.5 text-muted"
                  >
                    #{name}
                  </span>
                ))}
              </div>
            </>
          )}
        </div>

        <div className="mt-4 flex gap-2">
          <button
            type="button"
            data-testid="quick-capture-submit"
            onClick={submit}
            disabled={!canSubmit}
            className="flex-1 rounded-[var(--radius)] bg-accent px-4 text-sm font-medium text-[var(--color-accent-contrast)] disabled:opacity-60"
            style={{ minHeight: 'var(--touch-target)' }}
          >
            Anlegen
          </button>
          <button
            type="button"
            onClick={onClose}
            className="rounded-[var(--radius)] border border-border px-4 text-sm"
            style={{ minHeight: 'var(--touch-target)' }}
          >
            Abbrechen
          </button>
        </div>
      </div>
    </div>
  );
}
